import { format, isToday, isYesterday, parseISO } from 'date-fns'

/**
 * Canonical standalone date: "07 May 2026" (zero-padded day, abbreviated
 * month, four-digit year). This is the house style for every user-visible
 * date in the LMS web app. Reach for one of the other helpers below only
 * when a surface genuinely needs a weekday, month-only, or range.
 *
 * Returns the input verbatim when it isn't an ISO date — supports chart
 * surfaces that may carry non-date labels (e.g. "2025-Q3" quarterly buckets).
 */
export function fmtDate(value: string | Date): string {
  const date = typeof value === 'string' ? parseISO(value) : value
  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' ? value : ''
  }
  return format(date, 'dd MMM yyyy')
}

/**
 * Submitted-timestamp label for list surfaces: "Today" / "Yesterday" when the
 * value falls on the viewer's current or previous local day, otherwise the
 * canonical "dd MMM yyyy". Keeps recent submissions instantly legible without
 * making the reader parse a date. Returns "—" for an empty/invalid value.
 */
export function fmtSubmitted(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const date = typeof value === 'string' ? parseISO(value) : value
  if (Number.isNaN(date.getTime())) return typeof value === 'string' ? value : '—'
  if (isToday(date)) return 'Today'
  if (isYesterday(date)) return 'Yesterday'
  return format(date, 'dd MMM yyyy')
}

/** Weekday + canonical date for calendar/tooltip surfaces: "Thu 07 May 2026". */
export function fmtDateWithWeekday(value: string | Date): string {
  const date = typeof value === 'string' ? parseISO(value) : value
  return format(date, 'EEE dd MMM yyyy')
}

/** Canonical date without weekday: "07 May 2026". */
export function fmtDateLong(value: string | Date): string {
  const date = typeof value === 'string' ? parseISO(value) : value
  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' ? value : ''
  }
  return format(date, 'dd MMM yyyy')
}

/** Month + year for calendar headers and section labels: "May 2026". */
export function fmtMonthYear(value: string | Date): string {
  const date = typeof value === 'string' ? parseISO(value) : value
  return format(date, 'MMMM yyyy')
}

/** Canonical date "dd MMM yyyy" — e.g. "07 May 2026". Used in date-range filter chips. */
export function formatDateDMY(value: string | Date): string {
  const date = typeof value === 'string' ? parseISO(value) : value
  return format(date, 'dd MMM yyyy')
}

/**
 * Date range in the canonical "dd MMM yyyy" house style, used app-wide:
 *   same day → "07 May 2026"
 *   range    → "07 May 2026 - 09 May 2026"
 */
export function fmtDateRange(start: string | Date, end: string | Date): string {
  const startDate = typeof start === 'string' ? parseISO(start) : start
  const endDate = typeof end === 'string' ? parseISO(end) : end
  if (format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')) {
    return format(startDate, 'dd MMM yyyy')
  }
  return `${format(startDate, 'dd MMM yyyy')} - ${format(endDate, 'dd MMM yyyy')}`
}

/**
 * Long-form date range using the canonical "dd MMM yyyy" house style:
 *   same day → "07 May 2026"
 *   range    → "07 May 2026 - 09 May 2026"
 */
export function fmtDateRangeLong(start: string | Date, end: string | Date): string {
  const startDate = typeof start === 'string' ? parseISO(start) : start
  const endDate = typeof end === 'string' ? parseISO(end) : end
  if (format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')) {
    return format(startDate, 'dd MMM yyyy')
  }
  return `${format(startDate, 'dd MMM yyyy')} - ${format(endDate, 'dd MMM yyyy')}`
}

const numberFormatter = new Intl.NumberFormat('en-MY', {
  maximumFractionDigits: 1,
})

export function fmtNumber(value: unknown): string {
  return numberFormatter.format(toNumber(value))
}

/**
 * Coerce any string-or-number-shaped value to a finite number, or 0.
 *
 * Why: the api serializes `Decimal` columns as JSON strings to preserve
 * `Numeric(N, M)` precision, but our generic `apiRequest<T>` doesn't
 * run runtime validation, so the strings reach .toFixed sites typed
 * as number. Use this anywhere we render a numeric balance/weight.
 */
export function toNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.length > 0) {
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

/**
 * Day count for display. Integers print as integers; fractional days
 * print with one decimal (the smallest unit is half a day). NaN /
 * null / negative falls back to "0".
 *
 * formatDays(16)        // "16"
 * formatDays("16.000")  // "16"   (api Decimal-as-string)
 * formatDays(4.5)       // "4.5"
 * formatDays(undefined) // "0"
 */
export function formatDays(value: unknown): string {
  const n = toNumber(value)
  if (n <= 0) return '0'
  if (Number.isInteger(n)) return String(n)
  return n.toFixed(1).replace(/\.0$/, '')
}

/** Priority-weight format. Three decimal places, defensive against strings. */
export function formatWeight(value: unknown): string {
  return toNumber(value).toFixed(3)
}
